import { useEffect, useRef } from "react";
import { getVersion } from "../api/Api";
import { VERSION, VERSION_SET } from "../version";
import { UpdateSW } from "../lib/update_sw";

/**
 * A hook that checks if the client version matches the server version
 * and triggers a service worker update if they don't match.
 *
 * Performs the check on mount and periodically thereafter.
 */
export const useVersionChecker = () => {
  // Flag to prevent multiple simultaneous version checks
  const isCheckingRef = useRef(false);

  useEffect(() => {
    const checkVersion = async () => {
      // Skip if already checking
      if (isCheckingRef.current) return;
      // only do this for staging and for production
      if (!VERSION_SET) return;

      try {
        isCheckingRef.current = true;
        // Get the server version
        const serverVersion = await getVersion();
        console.log("Server version:", serverVersion);
        console.log("Client version:", VERSION);

        // If versions don't match, update the service worker
        if (serverVersion && serverVersion !== VERSION) {
          console.log("Version mismatch detected, updating service worker...");
          await UpdateSW();
          // The page should refresh soon after UpdateSW is called
        }
      } catch (error) {
        console.error("Error checking version:", error);
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Check immediately on mount
    checkVersion();

    const oneSecond = 1000;
    const oneMinute = 60 * oneSecond;
    // Set up periodic checks
    const intervalId = setInterval(() => {
      checkVersion();
    }, oneMinute); // Check every minute if it's time for a version check

    return () => {
      clearInterval(intervalId);
    };
  }, []);
};

export default useVersionChecker;
